/**
 * Sessions API endpoints — tricorder-session's surface, reached through the
 * gateway's /sessions/** proxy from the cut-over on (tricorder-session
 * SPEC.md sections 2, 3, 7). Creating a session on an agent RUNS it: POST
 * /sessions takes the agent and the first message and answers 202 with both.
 * A session that pins no agent (a protocol run) is created alone, 201, and
 * written by typed appends.
 */

import { api } from '../client';
import {
  SessionResponseSchema,
  SessionResponsePageSchema,
  MessageResponsePageSchema,
  MessageResponseSchema,
  CreateSessionResponseSchema,
  CreateAgentlessSessionResponseSchema,
  SessionSummaryResponseSchema,
  PromptPreviewResponseSchema,
  PromptRecordPageSchema,
  SessionTracePageSchema,
  SessionShareListSchema,
  SessionElicitationPageSchema,
  SessionFeedbackPageSchema,
} from '../schemas_session';
import type { ResolveElicitationRequest, SessionStatus } from '../types_session';
import { parseResponse } from '../validation';
import { randomUUID } from '@/lib/uuid';

/**
 * Replace what a client writes on a session: its title and its status, whole
 * (PUT, never conditional). An archived session takes no write (409).
 */
function replaceSession(id: string, title: string, status: SessionStatus) {
  return api.put(`/sessions/${id}`, { title, status }).then(
    (res) => parseResponse(SessionResponseSchema, res, `PUT /sessions/${id}`),
  );
}

export const sessionsApi = {
  /**
   * The first page of the caller's sessions, most recent activity first.
   * `status` narrows to one lifecycle status; absent lists both. Later pages
   * are the `Link: rel="next"` the page carries, never built here.
   */
  list: (status?: SessionStatus) =>
    api.getPage(`/sessions${status ? `?status=${status}` : ''}`, { schema: SessionResponsePageSchema }),

  /** Get one session. Messages read separately (`messages`, below). */
  get: (id: string) =>
    api.get(`/sessions/${id}`).then(
      (res) => parseResponse(SessionResponseSchema, res, `GET /sessions/${id}`),
    ),

  /**
   * The first page of the session's messages after `after`, in seq order,
   * oldest first. `after` is the catch-up cursor: pass the seq the last event
   * named (SPEC.md section 7). `next` is the page after it.
   */
  messages: (id: string, after = 0) =>
    api.getPage(`/sessions/${id}/messages?after=${after}`, {
      schema: MessageResponsePageSchema,
    }),

  /**
   * Every message after `after`, in seq order, following the list's
   * `Link: rel="next"` (tricorder-session SPEC.md section 2) to its last page.
   */
  messagesAfter: (id: string, after: number) =>
    api.getAllItems(`/sessions/${id}/messages?after=${after}`, {
      schema: MessageResponsePageSchema,
    }),

  /** One message by id. */
  message: (id: string, messageId: string) =>
    api.get(`/sessions/${id}/messages/${messageId}`),

  /**
   * Per-turn traces for one session (the per-session trace read — which
   * tools/services fired per turn).
   */
  traces: (id: string) =>
    api.getPage(`/sessions/${id}/traces`, { schema: SessionTracePageSchema }),

  /** The prompt records of the session's turns, one per turn. */
  promptRecords: (id: string) =>
    api.getPage(`/sessions/${id}/prompt-records`, {
      schema: PromptRecordPageSchema,
    }),

  /**
   * The prompt the session's next turn would receive, rendered with the one
   * harness renderer from the pinned agent version.
   */
  prompt: (id: string) =>
    api.get(`/sessions/${id}/prompt`).then(
      (res) => parseResponse(PromptPreviewResponseSchema, res, `GET /sessions/${id}/prompt`),
    ),

  /**
   * Create a session on an agent and RUN it: the first message is required,
   * and the session pins the agent version in force now. `subject` cites the
   * party the session is about (e.g. `{type: 'patient', id}`).
   */
  create: (
    agentSlug: string,
    firstMessage: string,
    options?: { title?: string; subject?: { type: string; id: string }; idempotencyKey?: string },
  ) =>
    api.post('/sessions', {
      agent: agentSlug,
      message: firstMessage,
      ...(options?.title ? { title: options.title } : {}),
      ...(options?.subject ? { subject: options.subject } : {}),
    }, {
      headers: { 'Idempotency-Key': options?.idempotencyKey ?? randomUUID() },
    }).then(
      (res) => parseResponse(CreateSessionResponseSchema, res, 'POST /sessions'),
    ),

  /**
   * Say something: write the user's message and queue the turn. The answer
   * arrives on GET /sessions/{id}/events and as the agent's message in the
   * session. A retried key answers the first call's message.
   */
  postMessage: (id: string, content: string, idempotencyKey?: string) =>
    api.post(`/sessions/${id}/messages`, { content }, {
      headers: { 'Idempotency-Key': idempotencyKey ?? randomUUID() },
    }).then(
      (res) => parseResponse(MessageResponseSchema, res, `POST /sessions/${id}/messages`),
    ),

  /**
   * Create a session that pins no agent (tricorder-session/ADR-0009): a
   * protocol run's log. No turn runs on it; its records are typed appends.
   * `subject` cites the party the run is about, `parent` the conversation it
   * was offered in. A retried key answers the first call's session.
   */
  createAgentless: (
    options: { subject?: { type: string; id: string }; parent?: string; title?: string },
    idempotencyKey: string,
  ) =>
    api.post('/sessions', {
      ...(options.subject ? { subject: options.subject } : {}),
      ...(options.parent ? { parent: options.parent } : {}),
      ...(options.title ? { title: options.title } : {}),
    }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then(
      (res) => parseResponse(CreateAgentlessSessionResponseSchema, res, 'POST /sessions (agentless)'),
    ),

  /**
   * Append a typed record (tricorder-session/ADR-0008): `type` names the
   * payload's schema, and no turn starts. A retry with the same key answers
   * the first call's message, so a lost answer never appends twice.
   */
  appendTyped: (id: string, type: string, payload: unknown, idempotencyKey: string) =>
    api.post(`/sessions/${id}/messages`, { type, payload }, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then(
      (res) => parseResponse(MessageResponseSchema, res, `POST /sessions/${id}/messages (${type})`),
    ),

  /**
   * Ask another agent: create a child session under the parent and run it
   * with the question as its first message (a consult, POST /sessions naming
   * `parent`; the server runs the delegation checks). The answer reaches the
   * parent as a message in the parent session.
   */
  spawn: (parentId: string, agentSlug: string, question: string, idempotencyKey?: string) =>
    api.post('/sessions', { agent: agentSlug, parent: parentId, message: question }, {
      headers: { 'Idempotency-Key': idempotencyKey ?? randomUUID() },
    }).then(
      (res) => parseResponse(CreateSessionResponseSchema, res, 'POST /sessions (consult)'),
    ),

  /** Rename an active session. */
  rename: (id: string, title: string) => replaceSession(id, title, 'active'),

  /**
   * Archive: the session's status, written with its title. An archived
   * session takes no message; open a new one.
   */
  archive: (id: string, title: string) => replaceSession(id, title, 'archived'),

  /** Erase (data-protection): deletes the session and all its records. */
  delete: (id: string) => api.delete(`/sessions/${id}`),

  /** The session's stored summary. */
  summary: (id: string) =>
    api.get(`/sessions/${id}/summary`).then(
      (res) => parseResponse(SessionSummaryResponseSchema, res, `GET /sessions/${id}/summary`),
    ),

  /** Record a finished child's answer into the parent. */
  recordChildSummary: (parentId: string, childSessionId: string) =>
    api.post(`/sessions/${parentId}/summary`, { child_session_id: childSessionId }),

  /** The session's shares. */
  shares: (id: string) =>
    api.get(`/sessions/${id}/shares`).then(
      (res) => parseResponse(SessionShareListSchema, res, `GET /sessions/${id}/shares`).items,
    ),

  share: (id: string, body: { principal_type: string; principal_id: string; role: string; expires_at?: string }) =>
    api.post(`/sessions/${id}/shares`, body),

  revokeShare: (id: string, principalType: string, principalId: string) =>
    api.delete(`/sessions/${id}/shares/${principalType}/${principalId}`),

  /** The session's elicitations (pending and answered). */
  elicitations: (id: string) =>
    api.getPage(`/sessions/${id}/elicitations`, { schema: SessionElicitationPageSchema }),

  /** Answer a blocked tool call's question; the turn resumes. */
  resolveElicitation: (id: string, body: ResolveElicitationRequest) =>
    api.post(`/sessions/${id}/elicitations`, body),

  /** Feedback on a message. */
  feedback: (id: string, messageId: string) =>
    api.getPage(`/sessions/${id}/messages/${messageId}/feedback`, {
      schema: SessionFeedbackPageSchema,
    }),

  createFeedback: (id: string, messageId: string, body: { type: string; text?: string }) =>
    api.post(`/sessions/${id}/messages/${messageId}/feedback`, body),
};
